import React, { useState } from "react";
import {
  Avatar,
  Typography,
  Divider,
  Box,
  Chip,
  Link,
  Card,
  Grid,
  IconButton,
  Dialog,
  DialogContent,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import WcIcon from "@mui/icons-material/Wc";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import PersonIcon from "@mui/icons-material/Person";
import CloseIcon from "@mui/icons-material/Close";

import { API_BASE_URL } from "../../../src/utils/api";
import ViolationsTimeline from "./ViolationsTimeline";
import MiniMap from "../shared/MiniMap";

const ViolationProfileCard = ({ member, violation_id }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [preview, setPreview] = useState({
    open: false,
    src: "",
    title: "",
  });

  const openPreview = (src, title) => {
    if (!src) return;
    setPreview({ open: true, src, title });
  };

  const closePreview = () =>
    setPreview((prev) => ({
      ...prev,
      open: false,
    }));

  if (!member) return null;

  const getFormattedDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const photoUrl = member.PhotoGraphicFileName?.trim?.()
    ? `${API_BASE_URL}/images/${encodeURIComponent(
        member.PhotoGraphicFileName.trim()
      )}`
    : undefined;

  const address = member.FullAddress?.trim() || "N/A";
  const mapsLink =
    address !== "N/A"
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          address
        )}`
      : null;

  const altAddress =
    member.AlternateAddress?.trim?.() ||
    (Array.isArray(member.AlternateAddresses) &&
    member.AlternateAddresses.length
      ? [
          member.AlternateAddresses[0]?.AddressLine1,
          member.AlternateAddresses[0]?.AddressLine2,
          member.AlternateAddresses[0]?.AddressLine3,
          member.AlternateAddresses[0]?.AddressLine4,
          member.AlternateAddresses[0]?.AddressLine5,
        ]
          .map((v) => (v || "").toString().trim())
          .filter(Boolean)
          .join(" ")
      : null);

  const altMapsLink = altAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        altAddress
      )}`
    : null;

  const phone = member.PhoneNumber?.trim() || null;
  const altPhone = member.AlternatePhoneNumber?.trim() || null;

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const infoRowSx = {
    display: "flex",
    alignItems: "flex-start",
    gap: 1,
    py: 0.75,
    minWidth: 0,
    height: "100%",
  };

  const iconWrapSx = {
    width: 34,
    height: 34,
    minWidth: 34,
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(47,99,173,0.08)",
    flexShrink: 0,
  };

  const labelSx = {
    color: "#8a94a6",
    display: "block",
    lineHeight: 1.2,
    mb: 0.25,
  };

  const valueSx = {
    color: "#223142",
    fontWeight: 600,
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    lineHeight: 1.3,
  };

  return (
    <>
      <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
        <Box
          sx={{
            px: 2.5,
            pt: 2.5,
            pb: 2,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
            textAlign: "center",
          }}
        >
          <Avatar
            src={photoUrl}
            alt={member.FirstName}
            onClick={() =>
              openPreview(
                photoUrl,
                `${member.FirstName || ""} ${member.LastName || ""}`.trim()
              )
            }
            sx={{
              width: 112,
              height: 112,
              margin: "0 auto",
              mb: 1.75,
              border: "4px solid rgba(47,99,173,0.16)",
              boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
              cursor: photoUrl ? "pointer" : "default",
            }}
          >
            {!photoUrl && (member.FirstName?.[0] || "?")}
          </Avatar>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#223142",
              lineHeight: 1.2,
            }}
          >
            {`${member.FirstName || ""} ${member.LastName || ""}`.trim() ||
              "Member"}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#6b778c",
              mt: 0.5,
            }}
          >
            Member Code: {member.ClubMemberCode || "N/A"}
          </Typography>

          <Box sx={{ mt: 1.5 }}>
            <Chip
              label={member.ClubStatusRuleCode || "Unknown"}
              color="success"
              size="small"
              icon={<VerifiedUserIcon sx={{ color: "white !important" }} />}
              sx={{
                fontWeight: 700,
                height: 30,
                borderRadius: "999px",
              }}
            />
          </Box>
        </Box>

        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <Box sx={infoRowSx}>
                <Box sx={iconWrapSx}>
                  <PersonIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={labelSx}>
                    Member
                  </Typography>
                  <Typography variant="body2" sx={valueSx}>
                    {member.ClubMemberCode || "N/A"}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={infoRowSx}>
                <Box sx={iconWrapSx}>
                  <WcIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={labelSx}>
                    Gender
                  </Typography>
                  <Typography variant="body2" sx={valueSx}>
                    {member.Gender || "N/A"}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={infoRowSx}>
                <Box sx={iconWrapSx}>
                  <CalendarTodayIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={labelSx}>
                    Birth Date
                  </Typography>
                  <Typography variant="body2" sx={valueSx}>
                    {getFormattedDate(member.BirthDate)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={infoRowSx}>
                <Box sx={iconWrapSx}>
                  <CalendarTodayIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={labelSx}>
                    Join Date
                  </Typography>
                  <Typography variant="body2" sx={valueSx}>
                    {getFormattedDate(member.JoinDate)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={infoRowSx}>
                <Box sx={iconWrapSx}>
                  <PhoneIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={labelSx}>
                    Phone
                  </Typography>
                  {phone ? (
                    <Link
                      href={`tel:${phone}`}
                      underline="hover"
                      sx={{
                        color: "#2f63ad",
                        fontSize: "0.95rem",
                        fontWeight: 600,
                        wordBreak: "break-word",
                      }}
                    >
                      {phone}
                    </Link>
                  ) : (
                    <Typography variant="body2" sx={valueSx}>
                      N/A
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={infoRowSx}>
                <Box sx={iconWrapSx}>
                  <PhoneAndroidIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={labelSx}>
                    Alt Phone
                  </Typography>
                  {altPhone ? (
                    <Link
                      href={`tel:${altPhone}`}
                      underline="hover"
                      sx={{
                        color: "#2f63ad",
                        fontSize: "0.95rem",
                        fontWeight: 600,
                        wordBreak: "break-word",
                      }}
                    >
                      {altPhone}
                    </Link>
                  ) : (
                    <Typography variant="body2" sx={valueSx}>
                      N/A
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={infoRowSx}>
                <Box sx={iconWrapSx}>
                  <LocationOnIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={labelSx}>
                    Address
                  </Typography>
                  {mapsLink ? (
                    <Link
                      href={mapsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{
                        color: "#2f63ad",
                        fontSize: "0.95rem",
                        fontWeight: 600,
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {address}
                    </Link>
                  ) : (
                    <Typography variant="body2" sx={valueSx}>
                      N/A
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            {member?.ClubMemberCode && (
              <Grid item xs={12}>
                <MiniMap clubMemberCode={member.ClubMemberCode} height={160} />
              </Grid>
            )}

            <Grid item xs={12}>
              <Box sx={infoRowSx}>
                <Box sx={iconWrapSx}>
                  <LocationOnIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={labelSx}>
                    Alternate Address
                  </Typography>
                  {altMapsLink ? (
                    <Link
                      href={altMapsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{
                        color: "#2f63ad",
                        fontSize: "0.95rem",
                        fontWeight: 600,
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {altAddress}
                    </Link>
                  ) : (
                    <Typography variant="body2" sx={valueSx}>
                      N/A
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2.25, borderColor: "rgba(47,99,173,0.08)" }} />

          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: "#223142",
                mb: 1.25,
              }}
            >
              Violation Timeline
            </Typography>

            <Box
              sx={{
                borderRadius: "14px",
                border: "1px solid rgba(47,99,173,0.08)",
                backgroundColor: "rgba(47,99,173,0.02)",
                p: 1.5,
              }}
            >
              <ViolationsTimeline violation_id={violation_id} />
            </Box>
          </Box>
        </Box>
      </Card>

      <Dialog
        open={preview.open}
        onClose={closePreview}
        fullScreen={fullScreen}
        maxWidth="md"
        fullWidth
      >
        <Box sx={{ position: "relative", bgcolor: "black" }}>
          <IconButton
            aria-label="Close"
            onClick={closePreview}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              zIndex: 1,
              bgcolor: "rgba(0,0,0,0.5)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            }}
          >
            <CloseIcon sx={{ color: "white" }} />
          </IconButton>

          <DialogContent
            sx={{
              p: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {preview.src ? (
              <Box
                component="img"
                src={preview.src}
                alt={preview.title || "Member photo"}
                sx={{
                  display: "block",
                  width: "100%",
                  maxHeight: { xs: "100vh", md: "80vh" },
                  objectFit: "contain",
                  backgroundColor: "black",
                }}
              />
            ) : (
              <Box sx={{ p: 4 }}>
                <Typography>No image available.</Typography>
              </Box>
            )}
          </DialogContent>
        </Box>
      </Dialog>
    </>
  );
};

export default ViolationProfileCard;